import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  API,
  apiErr,
  bracketMatchToPutBody,
  formatCrossGroupRankLabel,
  formatGroupPositionLabel,
  seedKnockoutBracket,
  toDateTimeLocalValue,
} from './tournamentSetupHelpers';
import { useConfirm } from '../../common/ConfirmDialog';

/** Fuentes mutuamente excluyentes para una plaza (local / visitante) en un partido de eliminatoria. */
export const BRACKET_SIDE_SOURCES = Object.freeze({
  TEAM: 'team',
  POSITION: 'pos',
  CROSS: 'cross',
});

function parseCrossInput(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n >= 1 ? n : NaN;
}

function deriveSourceMode({ teamId, groupPositionId, crossPos, crossIdx }) {
  if (crossPos && crossIdx) return BRACKET_SIDE_SOURCES.CROSS;
  if (groupPositionId) return BRACKET_SIDE_SOURCES.POSITION;
  if (teamId) return BRACKET_SIDE_SOURCES.TEAM;
  return BRACKET_SIDE_SOURCES.TEAM;
}

function parseBracketFeederOrderInput(raw) {
  const n = parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

function isFeederOrderTaken(bracketMatches, nextMatchId, order, excludeMatchId) {
  return bracketMatches.some(
    (m) =>
      m.nextMatchId === nextMatchId &&
      m.order != null &&
      Number(m.order) === order &&
      (!excludeMatchId || m.id !== excludeMatchId)
  );
}

/**
 * Orden 1 = local del destino entre feeders; 2 = visitante. Solo mira si otro partido ya usa ese orden hacia el mismo
 * destino (no las plazas del partido destino: una final puede tener ya local/visitante por grupos y aquí sigue eligiendo 1 o 2).
 */
function getFeederSlotAvailability(bracketMatches, nextMatchId, excludeMatchId) {
  if (!nextMatchId) {
    return { allowOrder1: false, allowOrder2: false, freeform: false };
  }
  const order1Taken = isFeederOrderTaken(bracketMatches, nextMatchId, 1, excludeMatchId);
  const order2Taken = isFeederOrderTaken(bracketMatches, nextMatchId, 2, excludeMatchId);
  const allowOrder1 = !order1Taken;
  const allowOrder2 = !order2Taken;
  const freeform = !allowOrder1 && !allowOrder2;
  return { allowOrder1, allowOrder2, freeform };
}

function pickDefaultFeederOrder(slots) {
  if (slots.freeform) return null;
  if (slots.allowOrder1 && !slots.allowOrder2) return '1';
  if (!slots.allowOrder1 && slots.allowOrder2) return '2';
  if (slots.allowOrder1 && slots.allowOrder2) return '1';
  return '';
}

/**
 * Estado y acciones de la pestaña Eliminatoria (fase final).
 * Los datos bracketMatches / teams / groupPositions vienen del padre (refreshAll).
 */
export function useTournamentBracketTab({
  tournamentId,
  teams,
  groupPositions,
  bracketMatches,
  refreshAll,
  setLoadError,
  setInfo,
  busy,
  setBusy,
}) {
  const confirm = useConfirm();
  const [koSize, setKoSize] = useState(8);
  const [koStart, setKoStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(18, 0, 0, 0);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  });
  const [koHoursStep, setKoHoursStep] = useState(24);

  const [koMatchName, setKoMatchName] = useState('');
  const [koMatchDayHour, setKoMatchDayHour] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(18, 0, 0, 0);
    return toDateTimeLocalValue(d);
  });
  const [koLocalTeamId, setKoLocalTeamId] = useState('');
  const [koForeignTeamId, setKoForeignTeamId] = useState('');
  const [koPosLocalId, setKoPosLocalId] = useState('');
  const [koPosForeignId, setKoPosForeignId] = useState('');
  const [koLocalSource, setKoLocalSource] = useState(BRACKET_SIDE_SOURCES.TEAM);
  const [koForeignSource, setKoForeignSource] = useState(BRACKET_SIDE_SOURCES.TEAM);
  const [koCrossPosLocal, setKoCrossPosLocal] = useState('');
  const [koCrossIdxLocal, setKoCrossIdxLocal] = useState('');
  const [koCrossPosForeign, setKoCrossPosForeign] = useState('');
  const [koCrossIdxForeign, setKoCrossIdxForeign] = useState('');
  const [koNextMatchId, setKoNextMatchId] = useState('');
  const [koFeederOrder, setKoFeederOrder] = useState('1');
  const [koFieldName, setKoFieldName] = useState('');

  const [connectFromId, setConnectFromId] = useState('');
  const [connectToId, setConnectToId] = useState('');
  const [connectFeederOrder, setConnectFeederOrder] = useState('1');

  const [bracketEditOpen, setBracketEditOpen] = useState(false);
  const [bracketEditBase, setBracketEditBase] = useState(null);
  const [beName, setBeName] = useState('');
  const [beDayHour, setBeDayHour] = useState('');
  const [beFieldName, setBeFieldName] = useState('');
  const [beLocalTeamId, setBeLocalTeamId] = useState('');
  const [beForeignTeamId, setBeForeignTeamId] = useState('');
  const [bePosLocalId, setBePosLocalId] = useState('');
  const [bePosForeignId, setBePosForeignId] = useState('');
  const [beLocalSource, setBeLocalSource] = useState(BRACKET_SIDE_SOURCES.TEAM);
  const [beForeignSource, setBeForeignSource] = useState(BRACKET_SIDE_SOURCES.TEAM);
  const [beCrossPosLocal, setBeCrossPosLocal] = useState('');
  const [beCrossIdxLocal, setBeCrossIdxLocal] = useState('');
  const [beCrossPosForeign, setBeCrossPosForeign] = useState('');
  const [beCrossIdxForeign, setBeCrossIdxForeign] = useState('');
  const [beNextMatchId, setBeNextMatchId] = useState('');
  const [beOrder, setBeOrder] = useState('');

  const sortedBracketMatches = useMemo(() => {
    return [...bracketMatches].sort((a, b) => new Date(a.dayHour) - new Date(b.dayHour));
  }, [bracketMatches]);

  // Agrupa los partidos por ronda. La ronda se deduce de la distancia hasta la final
  // siguiendo los enlaces `nextMatchId`: la final está a distancia 0, las semifinales a 1,
  // los cuartos a 2, los octavos a 3, etc.
  const bracketRounds = useMemo(() => {
    const ROUND_NAMES = [
      'Final',
      'Semifinales',
      'Cuartos de final',
      'Octavos de final',
      'Dieciseisavos de final',
    ];
    const byId = new Map(bracketMatches.map((m) => [m.id, m]));
    const distanceToFinal = (match) => {
      let distance = 0;
      let current = match;
      const guard = new Set();
      while (current && current.nextMatchId && !guard.has(current.id)) {
        guard.add(current.id);
        current = byId.get(current.nextMatchId);
        distance += 1;
      }
      return distance;
    };

    const buckets = new Map();
    for (const m of bracketMatches) {
      const d = distanceToFinal(m);
      if (!buckets.has(d)) buckets.set(d, []);
      buckets.get(d).push(m);
    }

    return [...buckets.entries()]
      .sort((a, b) => b[0] - a[0]) // la ronda más lejana de la final (octavos…) primero
      .map(([distance, list]) => ({
        key: `round-${distance}`,
        title: ROUND_NAMES[distance] || `Ronda ${distance + 1}`,
        matches: list
          .slice()
          .sort(
            (a, b) =>
              new Date(a.dayHour) - new Date(b.dayHour) ||
              String(a.name).localeCompare(String(b.name), 'es', { numeric: true }),
          ),
      }));
  }, [bracketMatches]);

  // Pending cross-group ties (only those affecting bracket slots). Fetched on mount and after
  // refreshAll-triggered renders so the banner appears as soon as a tie shows up in the standings.
  const [crossGroupTies, setCrossGroupTies] = useState([]);
  const reloadCrossGroupTies = useCallback(async () => {
    if (!tournamentId) return;
    try {
      const { data } = await axios.get(`${API}/api/tournaments/${tournamentId}/cross-group-ties`);
      setCrossGroupTies(Array.isArray(data) ? data : []);
    } catch (err) {
      setCrossGroupTies([]);
    }
  }, [tournamentId]);
  useEffect(() => { reloadCrossGroupTies(); }, [reloadCrossGroupTies, bracketMatches]);

  // Resolve-tie dialog state. The admin orders the tied teams; saving PUTs the full ordering.
  const [tieDialogOpen, setTieDialogOpen] = useState(false);
  const [tieDialogTie, setTieDialogTie] = useState(null);
  const [tieDialogOrder, setTieDialogOrder] = useState([]); // team ids, top-to-bottom
  const openTieDialog = useCallback((tie) => {
    setTieDialogTie(tie);
    setTieDialogOrder(tie.tiedTeams.map((t) => t.teamId));
    setTieDialogOpen(true);
  }, []);
  const closeTieDialog = useCallback(() => {
    setTieDialogOpen(false);
    setTieDialogTie(null);
    setTieDialogOrder([]);
  }, []);
  const moveTieEntry = useCallback((teamId, direction) => {
    setTieDialogOrder((prev) => {
      const idx = prev.indexOf(teamId);
      if (idx < 0) return prev;
      const swap = direction === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  }, []);
  const saveTieOrder = useCallback(async () => {
    if (!tieDialogTie) return;
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      await axios.put(
        `${API}/api/tournaments/${tournamentId}/cross-group-ranks/${tieDialogTie.positionNumber}`,
        { orderedTeamIds: tieDialogOrder }
      );
      setInfo('Orden del pool guardado. La fase final se actualiza con tu elección.');
      closeTieDialog();
      await reloadCrossGroupTies();
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  }, [tieDialogTie, tieDialogOrder, tournamentId, setBusy, setLoadError, setInfo, closeTieDialog, reloadCrossGroupTies, refreshAll]);

  const bracketNextLabel = useCallback(
    (nextId) => {
      if (!nextId) return '—';
      const m = bracketMatches.find((x) => x.id === nextId);
      return m?.name || String(nextId).slice(0, 8);
    },
    [bracketMatches]
  );

  const incomingBracketFeeds = useMemo(() => {
    const map = new Map();
    for (const m of bracketMatches) {
      if (!m.nextMatchId) continue;
      const list = map.get(m.nextMatchId) || [];
      list.push(m);
      map.set(m.nextMatchId, list);
    }
    return map;
  }, [bracketMatches]);

  const handleAddBracketMatch = async (e) => {
    e.preventDefault();
    const name = koMatchName.trim();
    if (!name) {
      setLoadError('Pon un nombre al partido (ej. Cuartos 1, Semifinal 2).');
      return;
    }
    const when = new Date(koMatchDayHour);
    if (Number.isNaN(when.getTime())) {
      setLoadError('Fecha u hora no válida.');
      return;
    }

    const localSide = buildSidePayload(
      koLocalSource,
      koLocalTeamId,
      koPosLocalId,
      koCrossPosLocal,
      koCrossIdxLocal,
      'local'
    );
    if (localSide.error) {
      setLoadError(localSide.error);
      return;
    }
    const foreignSide = buildSidePayload(
      koForeignSource,
      koForeignTeamId,
      koPosForeignId,
      koCrossPosForeign,
      koCrossIdxForeign,
      'visitante'
    );
    if (foreignSide.error) {
      setLoadError(foreignSide.error);
      return;
    }
    if (
      localSide.teamId
      && foreignSide.teamId
      && localSide.teamId === foreignSide.teamId
    ) {
      setLoadError('Local y visitante no pueden ser el mismo equipo.');
      return;
    }
    if (
      localSide.crossPos != null
      && foreignSide.crossPos != null
      && localSide.crossPos === foreignSide.crossPos
      && localSide.crossIdx === foreignSide.crossIdx
    ) {
      setLoadError('La regla «mejor entre grupos» de local y visitante no puede ser la misma.');
      return;
    }

    const nextId = koNextMatchId || null;
    let orderPayload = null;
    if (nextId) {
      const ord = parseBracketFeederOrderInput(koFeederOrder);
      if (ord == null) {
        setLoadError(
          'Si eliges «siguiente partido», indica un orden entero ≥ 1 (1 = el ganador entra como local del destino, 2 = visitante, etc.).'
        );
        return;
      }
      if (isFeederOrderTaken(bracketMatches, nextId, ord, null)) {
        setLoadError(`Ese orden (${ord}) ya lo usa otro partido enlazado al mismo destino; elige otro.`);
        return;
      }
      orderPayload = ord;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      await axios.post(`${API}/api/tournamentmatches/bracketmatch`, {
        tournamentId,
        name,
        // wall-clock local, sin convertir a UTC (ver tournamentSetupHelpers)
        dayHour: koMatchDayHour,
        nextMatchId: nextId,
        localTeamId: localSide.teamId,
        foreignTeamId: foreignSide.teamId,
        groupPositionLocalId: localSide.posId,
        groupPositionForeignId: foreignSide.posId,
        crossGroupRankPositionLocal: localSide.crossPos,
        crossGroupRankIndexLocal: localSide.crossIdx,
        crossGroupRankPositionForeign: foreignSide.crossPos,
        crossGroupRankIndexForeign: foreignSide.crossIdx,
        fieldName: koFieldName.trim() || null,
        order: orderPayload,
      });
      setInfo('Partido de eliminatoria creado.');
      setKoMatchName('');
      setKoLocalTeamId('');
      setKoForeignTeamId('');
      setKoPosLocalId('');
      setKoPosForeignId('');
      setKoCrossPosLocal('');
      setKoCrossIdxLocal('');
      setKoCrossPosForeign('');
      setKoCrossIdxForeign('');
      setKoLocalSource(BRACKET_SIDE_SOURCES.TEAM);
      setKoForeignSource(BRACKET_SIDE_SOURCES.TEAM);
      setKoNextMatchId('');
      setKoFeederOrder('1');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  function buildSidePayload(source, teamId, posId, crossPosRaw, crossIdxRaw, label) {
    if (source === BRACKET_SIDE_SOURCES.CROSS) {
      const pos = parseCrossInput(crossPosRaw);
      const idx = parseCrossInput(crossIdxRaw);
      if (pos == null || idx == null || Number.isNaN(pos) || Number.isNaN(idx)) {
        return { error: `Para «mejor entre grupos» en ${label}, indica posición e índice (enteros ≥ 1).` };
      }
      return { teamId: null, posId: null, crossPos: pos, crossIdx: idx };
    }
    if (source === BRACKET_SIDE_SOURCES.POSITION) {
      return { teamId: null, posId: posId || null, crossPos: null, crossIdx: null };
    }
    return { teamId: teamId || null, posId: null, crossPos: null, crossIdx: null };
  }

  const handleDeleteBracketMatch = async (matchId) => {
    setBusy(true);
    setLoadError('');
    try {
      const { data } = await axios.delete(`${API}/api/tournamentmatches/${matchId}/finalmatch`);
      if (data?.requiresConfirmation) {
        setBusy(false);
        if (!(await confirm({
          title: 'Eliminar partido',
          message: data.warningMessage || '¿Eliminar este partido de la fase final?',
          confirmText: 'Eliminar',
          tone: 'danger',
        }))) return;
        setBusy(true);
        await axios.delete(`${API}/api/tournamentmatches/${matchId}/finalmatch?confirmed=true`);
      }
      setInfo('Partido de eliminatoria eliminado.');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const openBracketEditDialog = (m) => {
    setBracketEditBase(m);
    setBeName(m.name || '');
    setBeDayHour(toDateTimeLocalValue(new Date(m.dayHour)));
    setBeFieldName(m.fieldName || '');
    setBeLocalTeamId(m.localTeamId || '');
    setBeForeignTeamId(m.foreignTeamId || '');
    setBePosLocalId(m.groupPositionLocalId || '');
    setBePosForeignId(m.groupPositionForeignId || '');
    setBeCrossPosLocal(m.crossGroupRankPositionLocal != null ? String(m.crossGroupRankPositionLocal) : '');
    setBeCrossIdxLocal(m.crossGroupRankIndexLocal != null ? String(m.crossGroupRankIndexLocal) : '');
    setBeCrossPosForeign(m.crossGroupRankPositionForeign != null ? String(m.crossGroupRankPositionForeign) : '');
    setBeCrossIdxForeign(m.crossGroupRankIndexForeign != null ? String(m.crossGroupRankIndexForeign) : '');
    setBeLocalSource(deriveSourceMode({
      teamId: m.localTeamId,
      groupPositionId: m.groupPositionLocalId,
      crossPos: m.crossGroupRankPositionLocal,
      crossIdx: m.crossGroupRankIndexLocal,
    }));
    setBeForeignSource(deriveSourceMode({
      teamId: m.foreignTeamId,
      groupPositionId: m.groupPositionForeignId,
      crossPos: m.crossGroupRankPositionForeign,
      crossIdx: m.crossGroupRankIndexForeign,
    }));
    const nextId = m.nextMatchId || '';
    setBeNextMatchId(nextId);
    const rawOrder = m.order != null && m.order !== '' ? String(m.order) : '';
    if (nextId) {
      const slots = getFeederSlotAvailability(bracketMatches, nextId, m.id);
      if (!slots.freeform) {
        const o = parseInt(rawOrder, 10);
        const valid =
          (o === 1 && slots.allowOrder1) || (o === 2 && slots.allowOrder2);
        setBeOrder(valid ? rawOrder : pickDefaultFeederOrder(slots) || '1');
      } else {
        setBeOrder(rawOrder);
      }
    } else {
      setBeOrder('');
    }
    setBracketEditOpen(true);
  };

  const closeBracketEditDialog = () => {
    setBracketEditOpen(false);
    setBracketEditBase(null);
  };

  const handleSaveBracketEdit = async () => {
    if (!bracketEditBase) return;
    const name = beName.trim();
    if (!name) {
      setLoadError('El nombre del partido no puede estar vacío.');
      return;
    }
    const when = new Date(beDayHour);
    if (Number.isNaN(when.getTime())) {
      setLoadError('Fecha u hora no válida.');
      return;
    }
    const localSide = buildSidePayload(
      beLocalSource,
      beLocalTeamId,
      bePosLocalId,
      beCrossPosLocal,
      beCrossIdxLocal,
      'local'
    );
    if (localSide.error) {
      setLoadError(localSide.error);
      return;
    }
    const foreignSide = buildSidePayload(
      beForeignSource,
      beForeignTeamId,
      bePosForeignId,
      beCrossPosForeign,
      beCrossIdxForeign,
      'visitante'
    );
    if (foreignSide.error) {
      setLoadError(foreignSide.error);
      return;
    }
    if (
      localSide.teamId
      && foreignSide.teamId
      && localSide.teamId === foreignSide.teamId
    ) {
      setLoadError('Local y visitante no pueden ser el mismo equipo.');
      return;
    }
    if (
      localSide.crossPos != null
      && foreignSide.crossPos != null
      && localSide.crossPos === foreignSide.crossPos
      && localSide.crossIdx === foreignSide.crossIdx
    ) {
      setLoadError('La regla «mejor entre grupos» de local y visitante no puede ser la misma.');
      return;
    }
    const editNextId = beNextMatchId || null;
    let editOrderPayload = null;
    if (editNextId) {
      const ord = parseBracketFeederOrderInput(beOrder);
      if (ord == null) {
        setLoadError(
          'Con «siguiente partido», el orden es obligatorio: entero ≥ 1 (1 = local en el destino, 2 = visitante, …).'
        );
        return;
      }
      if (isFeederOrderTaken(bracketMatches, editNextId, ord, bracketEditBase.id)) {
        setLoadError(`Ese orden (${ord}) ya lo usa otro partido enlazado al mismo destino.`);
        return;
      }
      editOrderPayload = ord;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      await axios.put(`${API}/api/tournamentmatches/${bracketEditBase.id}/finalmatch`, {
        tournamentId,
        name,
        // wall-clock local, sin convertir a UTC (ver tournamentSetupHelpers)
        dayHour: beDayHour,
        nextMatchId: editNextId,
        localTeamId: localSide.teamId,
        foreignTeamId: foreignSide.teamId,
        groupPositionLocalId: localSide.posId,
        groupPositionForeignId: foreignSide.posId,
        crossGroupRankPositionLocal: localSide.crossPos,
        crossGroupRankIndexLocal: localSide.crossIdx,
        crossGroupRankPositionForeign: foreignSide.crossPos,
        crossGroupRankIndexForeign: foreignSide.crossIdx,
        fieldName: beFieldName.trim() || null,
        winnerTeamId: bracketEditBase.winnerTeamId ?? null,
        localGoals: bracketEditBase.localGoals ?? null,
        foreignGoals: bracketEditBase.foreignGoals ?? null,
        isActive: Boolean(bracketEditBase.isActive),
        completed: Boolean(bracketEditBase.completed),
        order: editOrderPayload,
      });
      setInfo('Partido de eliminatoria actualizado.');
      closeBracketEditDialog();
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleConnectBracketMatches = async () => {
    if (!connectFromId || !connectToId) {
      setLoadError('Elige partido origen y destino.');
      return;
    }
    if (connectFromId === connectToId) {
      setLoadError('Origen y destino deben ser distintos.');
      return;
    }
    const origin = bracketMatches.find((x) => x.id === connectFromId);
    const target = bracketMatches.find((x) => x.id === connectToId);
    if (!origin || !target) return;
    const connOrd = parseBracketFeederOrderInput(connectFeederOrder);
    if (connOrd == null) {
      setLoadError(
        'Indica el orden en el destino: entero ≥ 1 (1 = el ganador entra como local, 2 = como visitante, …).'
      );
      return;
    }
    if (isFeederOrderTaken(bracketMatches, target.id, connOrd, origin.id)) {
      setLoadError(`Ese orden (${connOrd}) ya lo usa otro partido enlazado a «${target.name}».`);
      return;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      await axios.put(`${API}/api/tournamentmatches/${origin.id}/finalmatch`, {
        ...bracketMatchToPutBody(origin),
        tournamentId: origin.tournamentId || tournamentId,
        nextMatchId: target.id,
        order: connOrd,
      });
      setInfo(`El ganador de «${origin.name}» pasa a «${target.name}» (orden ${connOrd}).`);
      setConnectFromId('');
      setConnectToId('');
      setConnectFeederOrder('1');
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnectBracketLink = async () => {
    if (!connectFromId) {
      setLoadError('Elige primero el partido origen.');
      return;
    }
    const origin = bracketMatches.find((x) => x.id === connectFromId);
    if (!origin?.nextMatchId) {
      setLoadError('Ese partido no tiene enlace «siguiente».');
      return;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      await axios.put(`${API}/api/tournamentmatches/${origin.id}/finalmatch`, {
        ...bracketMatchToPutBody(origin),
        tournamentId: origin.tournamentId || tournamentId,
        nextMatchId: null,
        order: null,
      });
      setInfo(`Se quitó el enlace desde «${origin.name}».`);
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleBeNextMatchIdChange = useCallback(
    (id) => {
      setBeNextMatchId(id);
      if (!id) {
        setBeOrder('');
        return;
      }
      const slots = getFeederSlotAvailability(bracketMatches, id, bracketEditBase?.id ?? null);
      const d = pickDefaultFeederOrder(slots);
      if (d != null) setBeOrder(d);
    },
    [bracketMatches, bracketEditBase?.id]
  );

  const handleKoNextMatchIdChange = useCallback(
    (id) => {
      setKoNextMatchId(id);
      if (!id) {
        setKoFeederOrder('1');
        return;
      }
      const slots = getFeederSlotAvailability(bracketMatches, id, null);
      const d = pickDefaultFeederOrder(slots);
      if (d != null) setKoFeederOrder(d);
    },
    [bracketMatches]
  );

  useEffect(() => {
    if (!connectToId) return;
    const slots = getFeederSlotAvailability(bracketMatches, connectToId, connectFromId || null);
    if (slots.freeform) return;
    if (slots.allowOrder1 && !slots.allowOrder2) setConnectFeederOrder('1');
    else if (!slots.allowOrder1 && slots.allowOrder2) setConnectFeederOrder('2');
    else
      setConnectFeederOrder((prev) => (prev === '2' && slots.allowOrder2 ? '2' : '1'));
  }, [connectToId, connectFromId, bracketMatches]);

  const editDestinationSlots = useMemo(
    () => getFeederSlotAvailability(bracketMatches, beNextMatchId, bracketEditBase?.id ?? null),
    [bracketMatches, beNextMatchId, bracketEditBase?.id]
  );

  const createDestinationSlots = useMemo(
    () => getFeederSlotAvailability(bracketMatches, koNextMatchId, null),
    [bracketMatches, koNextMatchId]
  );

  const connectDestinationSlots = useMemo(() => {
    if (!connectToId) return { allowOrder1: false, allowOrder2: false, freeform: false };
    return getFeederSlotAvailability(bracketMatches, connectToId, connectFromId || null);
  }, [bracketMatches, connectToId, connectFromId]);

  const handleSeedKnockout = async () => {
    if (!(await confirm({
      title: 'Crear cuadro de eliminatoria',
      message: `Se creará el cuadro para ${koSize} equipos con los partidos vacíos (sin equipos asignados todavía).`,
      confirmText: 'Crear cuadro',
    }))) {
      return;
    }
    setBusy(true);
    setLoadError('');
    setInfo('');
    try {
      // wall-clock local: pasamos el string tal cual del datetime-local input
      const n = await seedKnockoutBracket(
        tournamentId,
        Number(koSize),
        koStart,
        koHoursStep
      );
      setInfo(`Eliminatoria creada: ${n} partidos. Revisa la lista de abajo o «Ver fase final».`);
      await refreshAll();
    } catch (err) {
      setLoadError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return {
    teams,
    groupPositions,
    bracketMatches,
    busy,
    sortedBracketMatches,
    bracketRounds,
    incomingBracketFeeds,
    bracketNextLabel,
    formatGroupPositionLabel,
    formatCrossGroupRankLabel,
    BRACKET_SIDE_SOURCES,
    koSize,
    setKoSize,
    koStart,
    setKoStart,
    koHoursStep,
    setKoHoursStep,
    koMatchName,
    setKoMatchName,
    koMatchDayHour,
    setKoMatchDayHour,
    koFieldName,
    setKoFieldName,
    koLocalTeamId,
    setKoLocalTeamId,
    koForeignTeamId,
    setKoForeignTeamId,
    koPosLocalId,
    setKoPosLocalId,
    koPosForeignId,
    setKoPosForeignId,
    koLocalSource,
    setKoLocalSource,
    koForeignSource,
    setKoForeignSource,
    koCrossPosLocal,
    setKoCrossPosLocal,
    koCrossIdxLocal,
    setKoCrossIdxLocal,
    koCrossPosForeign,
    setKoCrossPosForeign,
    koCrossIdxForeign,
    setKoCrossIdxForeign,
    koNextMatchId,
    setKoNextMatchId,
    handleKoNextMatchIdChange,
    koFeederOrder,
    setKoFeederOrder,
    connectFromId,
    setConnectFromId,
    connectToId,
    setConnectToId,
    connectFeederOrder,
    setConnectFeederOrder,
    bracketEditOpen,
    bracketEditBase,
    beName,
    setBeName,
    beDayHour,
    setBeDayHour,
    beFieldName,
    setBeFieldName,
    beLocalTeamId,
    setBeLocalTeamId,
    beForeignTeamId,
    setBeForeignTeamId,
    bePosLocalId,
    setBePosLocalId,
    bePosForeignId,
    setBePosForeignId,
    beLocalSource,
    setBeLocalSource,
    beForeignSource,
    setBeForeignSource,
    beCrossPosLocal,
    setBeCrossPosLocal,
    beCrossIdxLocal,
    setBeCrossIdxLocal,
    beCrossPosForeign,
    setBeCrossPosForeign,
    beCrossIdxForeign,
    setBeCrossIdxForeign,
    beNextMatchId,
    setBeNextMatchId,
    handleBeNextMatchIdChange,
    editDestinationSlots,
    createDestinationSlots,
    connectDestinationSlots,
    beOrder,
    setBeOrder,
    handleAddBracketMatch,
    handleDeleteBracketMatch,
    openBracketEditDialog,
    closeBracketEditDialog,
    handleSaveBracketEdit,
    handleConnectBracketMatches,
    handleDisconnectBracketLink,
    handleSeedKnockout,
    crossGroupTies,
    reloadCrossGroupTies,
    tieDialogOpen,
    tieDialogTie,
    tieDialogOrder,
    openTieDialog,
    closeTieDialog,
    moveTieEntry,
    saveTieOrder,
  };
}
